import React, { useEffect, useState } from "react";
import {
  Loader2,
  Search,
  Plus,
  Pencil,
  Trash2,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Mail,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  useLazyGetEmailCategoriesQuery,
  useDeleteEmailCategoryMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";

import { IEmailCategory } from "../../../types/Request/AdminRequest";

const EmailCategoriesScreen: React.FC = () => {
  const navigate = useNavigate();

  const [getCategories, { data: categories = [], isFetching, isError }] =
    useLazyGetEmailCategoriesQuery();

  const [deleteCategory, { isLoading: isDeleting }] =
    useDeleteEmailCategoryMutation();

  const [search, setSearch] = useState("");

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const handleCreate = () => {
    navigate("/admin/adminemailtemplates/categories/create");
  };

  const handleEdit = (id: number) => {
    navigate(`/admin/adminemailtemplates/categories/edit/${id}`);
  };

  const handleDelete = async (category: IEmailCategory) => {
    if (category.isSystem) {
      return;
    }

    if ((category.templateCount ?? 0) > 0) {
      window.alert(
        "This category cannot be deleted because it is assigned to one or more email templates.",
      );

      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${category.displayName}"?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteCategory(category.id).unwrap();

      await getCategories();
    } catch (error) {
      console.error("Failed to delete email category:", error);
    }
  };

  const filteredCategories = categories.filter((category) => {
    const searchValue = search.trim().toLowerCase();

    if (!searchValue) {
      return true;
    }

    return (
      category.name.toLowerCase().includes(searchValue) ||
      category.displayName.toLowerCase().includes(searchValue) ||
      category.description.toLowerCase().includes(searchValue)
    );
  });

  if (isFetching && categories.length === 0) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={30} className="animate-spin text-primary" />

          <p className="text-sm text-slate-500">Loading email categories...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-white shadow-sm">
        <div className="flex min-h-[360px] flex-col items-center justify-center p-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-red-50 text-red-500">
            <XCircle size={26} />
          </div>

          <h2 className="mt-4 text-lg font-semibold text-slate-900">
            Unable to load categories
          </h2>

          <p className="mt-1 max-w-md text-sm text-slate-500">
            We couldn't retrieve the email categories. Please try again.
          </p>

          <button
            type="button"
            onClick={() => getCategories()}
            className="mt-5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ========================================================= */}
      {/* TOOLBAR */}
      {/* ========================================================= */}

      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search
            size={17}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories..."
            className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/10"
          />
        </div>

        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 active:scale-[0.98]"
        >
          <Plus size={17} />
          New Category
        </button>
      </div>

      {/* ========================================================= */}
      {/* TABLE */}
      {/* ========================================================= */}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80">
                <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Category
                </th>

                <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Description
                </th>

                <th className="px-5 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Templates
                </th>

                <th className="px-5 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Type
                </th>

                <th className="px-5 py-3.5 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                </th>

                <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {filteredCategories.map((category) => {
                const canDelete =
                  !category.isSystem && (category.templateCount ?? 0) === 0;

                return (
                  <tr
                    key={category.id}
                    className="transition hover:bg-slate-50/70"
                  >
                    {/* Category */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                          {category.isSystem ? (
                            <ShieldCheck size={19} />
                          ) : (
                            <Mail size={19} />
                          )}
                        </div>

                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {category.displayName}
                          </p>

                          <p className="mt-0.5 text-xs text-slate-400">
                            {category.name}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Description */}
                    <td className="max-w-[320px] px-5 py-4">
                      <p className="truncate text-sm text-slate-500">
                        {category.description || "—"}
                      </p>
                    </td>

                    {/* Templates */}
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex min-w-8 items-center justify-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                        {category.templateCount ?? 0}
                      </span>
                    </td>

                    {/* Type */}
                    <td className="px-5 py-4 text-center">
                      {category.isSystem ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600">
                          <ShieldCheck size={13} />
                          System
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                          Custom
                        </span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4 text-center">
                      {category.isActive ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-600">
                          <CheckCircle2 size={13} />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
                          <XCircle size={13} />
                          Inactive
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(category.id)}
                          title="Edit category"
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(category)}
                          disabled={!canDelete || isDeleting}
                          title={
                            category.isSystem
                              ? "System categories cannot be deleted"
                              : (category.templateCount ?? 0) > 0
                                ? "Category is assigned to email templates"
                                : "Delete category"
                          }
                          className={`flex h-9 w-9 items-center justify-center rounded-lg border transition ${
                            canDelete
                              ? "border-red-200 bg-white text-red-500 hover:bg-red-50"
                              : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300"
                          }`}
                        >
                          {isDeleting ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {filteredCategories.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="flex min-h-[280px] flex-col items-center justify-center p-8 text-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
                        <Search size={25} />
                      </div>

                      <h3 className="mt-4 text-sm font-semibold text-slate-800">
                        No categories found
                      </h3>

                      <p className="mt-1 text-xs text-slate-500">
                        Try changing your search or create a new category.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-3">
          <p className="text-xs text-slate-400">
            Showing{" "}
            <span className="font-semibold text-slate-600">
              {filteredCategories.length}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-slate-600">
              {categories.length}
            </span>{" "}
            categories
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailCategoriesScreen;
